import React from "react";
import { useHistory } from "react-router-dom";
import { OBJECT_TYPE } from "common/constants";
import { buildLink } from "common/routing";
import {
  AccountDocumentTemplate,
  useGenerateDocumentFromTemplateMutation,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import SelectionModal from "./SelectionModal";

export type GenerateDocumentProps = {
  submission: {
    id: string;
    documentTemplates?: Array<Pick<AccountDocumentTemplate, "id" | "name">>;
  };
  object: { id: string; objectType: OBJECT_TYPE };
  subtitle: string;
  prevLocation?: string;
  closeModal: () => void;
};

const GenerateDocument = ({
  submission,
  object: { id: objectId, objectType },
  subtitle,
  closeModal,
}: GenerateDocumentProps) => {
  const { addErrorToast, addSuccessToast } = useStatusToasts();
  const history = useHistory();

  const [generateDocument, { loading: loadingGenerateDocument }] =
    useGenerateDocumentFromTemplateMutation({
      onCompleted: data => {
        closeModal();
        addSuccessToast("Your document was successfully generated");

        history.push(
          buildLink("documentUploadDetail", {
            id: data.generateDocumentFromTemplate.id,
          }),
        );
      },
      onError: () => {
        const message =
          "There was an issue generating your document. Please refresh the page and try again. If the problem persists, please email us at support@withforerunner.com";
        addErrorToast(message);
      },
    });

  const handleSubmit = async (
    selectedTemplate: Pick<AccountDocumentTemplate, "id" | "name">,
  ): Promise<void> => {
    await generateDocument({
      variables: {
        templateId: selectedTemplate.id,
        objectId,
        objectType,
        submissionId: submission.id,
      },
    });
  };

  return (
    <SelectionModal
      title="Generate a file"
      subtitle={subtitle}
      options={submission.documentTemplates ?? []}
      primaryButtonText="Generate"
      closeModal={closeModal}
      onSubmit={handleSubmit}
      loading={loadingGenerateDocument}
    />
  );
};

export default GenerateDocument;
